/**
 * Tipos TypeScript para WebRTC
 * Define interfaces para signaling, estados e payloads
 * 
 * @author Sistema
 * @version 1.0.0
 */

// ============================================================================
// TIPOS DE ROLES E ESTADOS
// ============================================================================

/**
 * Papel do participante na sessão WebRTC
 */
export type WebRTCRole = 'agent' | 'client';

/**
 * Estados possíveis da sessão de screen share
 */
export type ScreenShareSessionState = 
  | 'idle'           // Nenhuma sessão ativa
  | 'requesting'     // Atendente solicitou, aguardando resposta
  | 'pending'        // Cliente recebeu solicitação, aguardando decisão
  | 'connecting'     // Ambos aceitaram, estabelecendo conexão P2P
  | 'connected'      // Conexão estabelecida, stream ativo
  | 'reconnecting'   // Tentando reconectar após falha
  | 'ended';         // Sessão encerrada

// ============================================================================
// PAYLOADS DE SIGNALING
// ============================================================================

/**
 * Payload do evento 'request' (Atendente → Cliente)
 * Enviado quando atendente solicita visualização de tela
 */
export interface RequestPayload {
  type: 'request';
  from: 'agent';
  agentName: string;
  agentAvatar?: string;
  timestamp: number;
}

/**
 * Payload do evento 'accept' (Cliente → Atendente)
 * Enviado quando cliente aceita a solicitação
 */
export interface AcceptPayload {
  type: 'accept';
  from: 'client';
  timestamp: number;
}

/**
 * Payload do evento 'reject' (Cliente → Atendente)
 * Enviado quando cliente recusa a solicitação
 */
export interface RejectPayload {
  type: 'reject';
  from: 'client';
  reason?: string;
  timestamp: number;
}

/**
 * Payload do evento 'offer' (Atendente → Cliente)
 * Contém o SDP offer para estabelecer conexão
 */
export interface OfferPayload {
  type: 'offer';
  from: 'agent';
  sdp: RTCSessionDescriptionInit;
  timestamp: number;
}

/**
 * Payload do evento 'answer' (Cliente → Atendente)
 * Contém o SDP answer em resposta ao offer
 */
export interface AnswerPayload {
  type: 'answer';
  from: 'client';
  sdp: RTCSessionDescriptionInit;
  timestamp: number;
}

/**
 * Payload do evento 'ice' (Bidirecional)
 * Contém candidato ICE para negociação de conexão
 */
export interface IceCandidatePayload {
  type: 'ice';
  from: WebRTCRole;
  candidate: RTCIceCandidateInit;
  timestamp: number;
}

/**
 * Payload do evento 'end' (Bidirecional)
 * Enviado para encerrar a sessão
 */
export interface EndPayload {
  type: 'end';
  from: WebRTCRole;
  reason?: 'user_action' | 'error' | 'timeout' | 'connection_failed';
  timestamp: number;
}

/**
 * União de todos os tipos de payload de signaling
 */
export type SignalingPayload = 
  | RequestPayload 
  | AcceptPayload 
  | RejectPayload 
  | OfferPayload 
  | AnswerPayload 
  | IceCandidatePayload 
  | EndPayload;

// ============================================================================
// INTERFACES DOS HOOKS
// ============================================================================

/**
 * Retorno do hook useScreenShare
 */
export interface UseScreenShareReturn {
  /** Stream de captura de tela atual (null se não está compartilhando) */
  stream: MediaStream | null;
  /** Indica se está compartilhando tela ativamente */
  isSharing: boolean;
  /** Mensagem de erro, se houver */
  error: string | null;
  /** Inicia captura de tela via getDisplayMedia - retorna o stream ou null se falhou */
  startSharing: () => Promise<MediaStream | null>;
  /** Para captura e libera recursos */
  stopSharing: () => void;
}

/**
 * Retorno do hook useWebRTC
 */
export interface UseWebRTCReturn {
  /** Estado atual da conexão P2P */
  connectionState: RTCPeerConnectionState;
  /** Stream de vídeo remoto recebido */
  remoteStream: MediaStream | null;
  /** Cria offer SDP (chamado pelo viewer/atendente) */
  createOffer: () => Promise<RTCSessionDescriptionInit>;
  /** Cria answer SDP em resposta a um offer (chamado pelo sharer/cliente) */
  createAnswer: (offer: RTCSessionDescriptionInit) => Promise<RTCSessionDescriptionInit>;
  /** Define a descrição remota (SDP recebido) */
  setRemoteDescription: (sdp: RTCSessionDescriptionInit) => Promise<void>;
  /** Adiciona candidato ICE remoto */
  addIceCandidate: (candidate: RTCIceCandidateInit) => Promise<void>;
  /** Adiciona stream local para transmissão (usado pelo sharer) */
  addLocalStream: (stream: MediaStream) => void;
  /** Fecha conexão e libera recursos */
  close: () => void;
}

/**
 * Callbacks de eventos para useWebRTCSignaling
 */
export interface SignalingEventHandlers {
  onRequest?: (data: RequestPayload) => void;
  onAccept?: (data: AcceptPayload) => void;
  onReject?: (data: RejectPayload) => void;
  onOffer?: (data: OfferPayload) => void;
  onAnswer?: (data: AnswerPayload) => void;
  onIce?: (data: IceCandidatePayload) => void;
  onEnd?: (data: EndPayload) => void;
}

/**
 * Retorno do hook useWebRTCSignaling
 */
export interface UseWebRTCSignalingReturn {
  /** Indica se o canal Realtime está conectado */
  isConnected: boolean;
  /** Envia solicitação de visualização (Atendente → Cliente) */
  sendRequest: (agentName: string, agentAvatar?: string) => void;
  /** Envia aceitação da solicitação (Cliente → Atendente) */
  sendAccept: () => void;
  /** Envia rejeição da solicitação (Cliente → Atendente) */
  sendReject: (reason?: string) => void;
  /** Envia offer SDP (Atendente → Cliente) */
  sendOffer: (sdp: RTCSessionDescriptionInit) => void;
  /** Envia answer SDP (Cliente → Atendente) */
  sendAnswer: (sdp: RTCSessionDescriptionInit) => void;
  /** Envia candidato ICE (Bidirecional) */
  sendIceCandidate: (candidate: RTCIceCandidateInit, from: WebRTCRole) => void;
  /** Envia evento de encerramento (Bidirecional) */
  sendEnd: (from: WebRTCRole, reason?: EndPayload['reason']) => void;
}

// ============================================================================
// CONFIGURAÇÕES
// ============================================================================

/**
 * Configuração de ICE servers (STUN apenas nesta fase)
 */
export const ICE_SERVERS_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
  iceCandidatePoolSize: 10,
};

/**
 * Constraints para captura de tela
 * Otimizado para suporte remoto (qualidade moderada, baixa latência)
 */
export const DISPLAY_MEDIA_CONSTRAINTS: DisplayMediaStreamOptions = {
  video: {
    frameRate: { ideal: 15, max: 30 },
    width: { max: 1920 },
    height: { max: 1080 },
  },
  audio: false, // Sem áudio para privacidade
};
